import { useEffect, useMemo, useState } from "react";
import type { ReactNode } from "react";
import {
  CalendarCheck,
  CalendarPlus,
  ChevronDown,
  Package,
  Plus,
  TriangleAlert,
} from "lucide-react";
import { toast } from "sonner";

import ModalBase from "components/ModalBase";
import ModalAgendarColeta from "./ModalAgendarColeta";
import {
  ColetaAgendada,
  cancelarColeta,
  listarColetasAgendadas,
} from "services/coletas/coletas.service";
import { getResumoCotacoes } from "services/cotacoes/cotacoes.service";
import { corTransportadora } from "utils/rastreioTransportadora";

type Props = {
  show: boolean;
  title: string;
  setShowNumber: (value: React.SetStateAction<number>) => void;
  logo?: ReactNode;
};

/* ------------------------------------------------------------------ */
/* Datas                                                               */
/* ------------------------------------------------------------------ */

const MESES = ["JAN", "FEV", "MAR", "ABR", "MAI", "JUN", "JUL", "AGO", "SET", "OUT", "NOV", "DEZ"];
const SEMANA = ["Dom", "Seg", "Ter", "Qua", "Qui", "Sex", "Sáb"];

/**
 * `pickupDate` vem como "YYYY-MM-DD" (DATEONLY).
 *
 * `new Date("2026-10-23")` seria lido como meia-noite UTC, que em Brasília é
 * dia 22 às 21h — a coleta apareceria um dia antes no cartão. Por isso monta
 * a data pelos componentes, no fuso local.
 */
const paraData = (iso: string | null): Date | null => {
  if (!iso) return null;
  const [y, m, d] = iso.slice(0, 10).split("-").map(Number);
  if (!y || !m || !d) return null;
  return new Date(y, m - 1, d);
};

const soDia = (d: Date) => new Date(d.getFullYear(), d.getMonth(), d.getDate());

/** "Hoje", "Amanhã" ou o dia da semana. */
const rotuloDia = (data: Date): string => {
  const hoje = soDia(new Date());
  const dias = Math.round((soDia(data).getTime() - hoje.getTime()) / 86400000);

  if (dias === 0) return "Hoje";
  if (dias === 1) return "Amanhã";
  if (dias === -1) return "Ontem";
  return SEMANA[data.getDay()];
};

const ehProxima = (coleta: ColetaAgendada): boolean => {
  if (String(coleta.status).toUpperCase() !== "AGENDADA") return false;
  const data = paraData(coleta.pickupDate);
  if (!data) return false;
  return soDia(data).getTime() >= soDia(new Date()).getTime();
};

const janela = (coleta: ColetaAgendada) => {
  const de = coleta.readyTime?.slice(0, 5);
  const ate = coleta.closeTime?.slice(0, 5);
  if (!de && !ate) return null;
  return `${de || "--:--"} – ${ate || "--:--"}`;
};

const dataHoraCurta = (iso: string) => {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return "—";
  return d.toLocaleString("pt-BR", {
    day: "2-digit",
    month: "short",
    hour: "2-digit",
    minute: "2-digit",
  });
};

const NOME_CARRIER: Record<string, string> = { FEDEX: "FedEx", UPS: "UPS" };
const SIGLA_CARRIER: Record<string, string> = { FEDEX: "FX", UPS: "UPS" };

/* ------------------------------------------------------------------ */
/* Modal                                                               */
/* ------------------------------------------------------------------ */

const ModalColetasAgendadas = ({ show, title, logo, setShowNumber }: Props) => {
  const [coletas, setColetas] = useState<ColetaAgendada[]>([]);
  const [carregando, setCarregando] = useState(false);
  const [aba, setAba] = useState<"proximas" | "historico">("proximas");
  const [expandida, setExpandida] = useState<number | null>(null);
  const [agendar, setAgendar] = useState(false);
  // Envios com etiqueta emitida que ainda não foram coletados — o CRIADO do
  // resumo de cotações. É o que dá sentido ao estado vazio: sem coleta
  // marcada, esses pacotes não saem do lugar.
  const [aguardandoColeta, setAguardandoColeta] = useState<number | null>(null);

  const carregar = async () => {
    setCarregando(true);
    try {
      const [lista, resumo] = await Promise.all([
        listarColetasAgendadas(),
        // Falha sozinho: é só o número da faixa de aviso, não vale derrubar a
        // listagem de coletas por causa dele.
        getResumoCotacoes().catch(() => null),
      ]);
      setColetas(lista);
      setAguardandoColeta(resumo ? (resumo.por_status.CRIADO ?? 0) : null);
    } catch {
      toast.error("Falha ao carregar as coletas agendadas.");
    } finally {
      setCarregando(false);
    }
  };

  useEffect(() => {
    if (!show) return;
    carregar();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [show]);

  useEffect(() => {
    if (show) return;
    setAba("proximas");
    setExpandida(null);
  }, [show]);

  const { proximas, historico } = useMemo(() => {
    const proximasLista = coletas.filter(ehProxima);
    // Ordem invertida da listagem: o back devolve pickup_date DESC, que para
    // o que ainda vai acontecer deixa a coleta mais distante no topo.
    proximasLista.sort(
      (a, b) =>
        (paraData(a.pickupDate)?.getTime() ?? 0) - (paraData(b.pickupDate)?.getTime() ?? 0),
    );
    return {
      proximas: proximasLista,
      historico: coletas.filter((c) => !ehProxima(c)),
    };
  }, [coletas]);

  const lista = aba === "proximas" ? proximas : historico;
  const vazio = !carregando && coletas.length === 0;

  const cancelar = async (id: number) => {
    try {
      await cancelarColeta(id);
      toast.success("Coleta marcada como cancelada");
      await carregar();
    } catch (err: any) {
      toast.error(err?.response?.data?.error || "Não foi possível cancelar a coleta");
    }
  };

  return (
    <>
      <ModalBase
        show={show}
        onClose={() => setShowNumber(-1)}
        titulo={title}
        icone={logo ?? <CalendarCheck />}
        largura="760px"
        corpoSemPadding
        subtitulo={
          carregando
            ? "Carregando…"
            : vazio
              ? "Acompanhe aqui suas coletas"
              : `${proximas.length} ${proximas.length === 1 ? "próxima" : "próximas"} · ${
                  historico.length
                } no histórico`
        }
        acoes={
          vazio ? undefined : (
            <button
              onClick={() => setAgendar(true)}
              className="flex h-9 items-center gap-2 rounded-lg bg-brand-sidebar px-3 text-sm font-semibold text-white transition-opacity hover:opacity-90"
            >
              <Plus className="h-4 w-4" />
              Agendar coleta
            </button>
          )
        }
      >
        {vazio ? (
          <EstadoVazio
            aguardando={aguardandoColeta}
            onAgendar={() => setAgendar(true)}
          />
        ) : (
          <>
            <div className="flex items-center gap-1 border-b border-slate-100 px-6">
              <Aba
                label="Próximas"
                n={proximas.length}
                ativa={aba === "proximas"}
                onClick={() => setAba("proximas")}
              />
              <Aba
                label="Histórico"
                n={historico.length}
                ativa={aba === "historico"}
                onClick={() => setAba("historico")}
              />
            </div>

            <div className="flex flex-col gap-3 px-6 py-4">
              {carregando && !coletas.length ? (
                <div className="h-24 animate-pulse rounded-xl bg-slate-100" />
              ) : !lista.length ? (
                <p className="py-10 text-center text-sm text-slate-400">
                  {aba === "proximas"
                    ? "Nenhuma coleta à frente"
                    : "Nenhuma coleta no histórico"}
                </p>
              ) : (
                lista.map((coleta) => (
                  <CartaoColeta
                    key={coleta.id}
                    coleta={coleta}
                    aberta={expandida === coleta.id}
                    onAlternar={() =>
                      setExpandida((atual) => (atual === coleta.id ? null : coleta.id))
                    }
                    onCancelar={() => cancelar(coleta.id)}
                  />
                ))
              )}
            </div>
          </>
        )}
      </ModalBase>

      {/* Empilhado aqui para o botão não depender de um canal de eventos
          entre o modal e o menu lateral. */}
      <ModalAgendarColeta
        show={agendar}
        title="Agendar coleta"
        setShowNumber={() => setAgendar(false)}
        logo={<CalendarPlus />}
      />
    </>
  );
};

export default ModalColetasAgendadas;

/* ------------------------------------------------------------------ */
/* Peças                                                               */
/* ------------------------------------------------------------------ */

const Aba = ({
  label,
  n,
  ativa,
  onClick,
}: {
  label: string;
  n: number;
  ativa: boolean;
  onClick: () => void;
}) => (
  <button
    onClick={onClick}
    className={`-mb-px flex items-center gap-1.5 border-b-2 px-3 py-2.5 text-sm transition-colors ${
      ativa
        ? "border-brand-sidebar font-semibold text-slate-900"
        : "border-transparent text-slate-500 hover:text-slate-700"
    }`}
  >
    {label}
    <span
      className={`rounded px-1.5 py-0.5 text-[10px] font-semibold ${
        ativa ? "bg-slate-900 text-white" : "bg-slate-100 text-slate-500"
      }`}
    >
      {n}
    </span>
  </button>
);

const CartaoColeta = ({
  coleta,
  aberta,
  onAlternar,
  onCancelar,
}: {
  coleta: ColetaAgendada;
  aberta: boolean;
  onAlternar: () => void;
  onCancelar: () => void;
}) => {
  const [confirmando, setConfirmando] = useState(false);
  const [cancelando, setCancelando] = useState(false);

  const data = paraData(coleta.pickupDate);
  const cancelada = String(coleta.status).toUpperCase() === "CANCELADA";
  const proxima = ehProxima(coleta);
  const cor = corTransportadora(coleta.carrier);
  const horario = janela(coleta);

  // Fechar o cartão tem que descartar a confirmação pendente, senão ela
  // reaparece aberta na próxima vez.
  useEffect(() => {
    if (!aberta) setConfirmando(false);
  }, [aberta]);

  const confirmar = async () => {
    setCancelando(true);
    await onCancelar();
    setCancelando(false);
    setConfirmando(false);
  };

  return (
    <div
      className={`overflow-hidden rounded-xl border transition-colors ${
        aberta ? "border-slate-300" : "border-slate-200"
      }`}
    >
      <div className="flex items-center gap-3 px-3 py-3">
        {/* Chip de data */}
        <div
          className={`flex h-14 w-14 shrink-0 flex-col items-center justify-center rounded-lg leading-none ${
            proxima ? "bg-brand-sidebar text-white" : "bg-slate-100 text-slate-500"
          }`}
        >
          {data ? (
            <>
              <span className="text-[9px] font-medium opacity-80">{rotuloDia(data)}</span>
              <span className="text-lg font-bold">{data.getDate()}</span>
              <span className="text-[9px] font-medium opacity-80">
                {MESES[data.getMonth()]}
              </span>
            </>
          ) : (
            <span className="text-[10px]">sem data</span>
          )}
        </div>

        <div className="min-w-0 flex-1">
          <div className="flex flex-wrap items-center gap-2">
            <span
              className="rounded px-1.5 py-0.5 text-[9px] font-bold text-white"
              style={{ backgroundColor: cor ?? "#64748b" }}
            >
              {SIGLA_CARRIER[coleta.carrier] ?? coleta.carrier}
            </span>
            <span className="text-sm font-semibold text-slate-900">
              {NOME_CARRIER[coleta.carrier] ?? coleta.carrier}
              {horario ? ` · ${horario}` : ""}
            </span>
            <span
              className={`inline-flex items-center gap-1.5 rounded-full px-2 py-0.5 text-[11px] font-medium ${
                cancelada
                  ? "bg-slate-100 text-slate-500"
                  : "bg-emerald-50 text-emerald-700"
              }`}
            >
              <span
                className={`h-1.5 w-1.5 rounded-full ${
                  cancelada ? "bg-slate-400" : "bg-emerald-500"
                }`}
              />
              {cancelada ? "Cancelada" : "Agendada"}
            </span>
          </div>

          <p className="mt-0.5 truncate text-xs text-slate-500">
            {[coleta.contactName, coleta.phone].filter(Boolean).join(" · ") ||
              coleta.endereco ||
              "—"}
          </p>
        </div>

        <button
          onClick={onAlternar}
          aria-label={aberta ? "Recolher" : "Ver detalhes"}
          className="grid h-8 w-8 shrink-0 place-items-center rounded-lg border border-slate-200 text-slate-500 transition-colors hover:bg-slate-50"
        >
          <ChevronDown
            className={`h-4 w-4 transition-transform ${aberta ? "rotate-180" : ""}`}
          />
        </button>
      </div>

      {aberta && (
        <div className="border-t border-slate-100 bg-slate-50/60 px-3 py-3">
          <p className="mb-2 text-[10px] font-semibold uppercase tracking-wider text-slate-400">
            Detalhes
          </p>
          <dl className="space-y-1.5 text-xs">
            <Linha termo="Confirmação" valor={coleta.confirmationNumber || "—"} mono />
            <Linha termo="Endereço" valor={coleta.endereco || "—"} />
            {coleta.contactName && <Linha termo="Contato" valor={coleta.contactName} />}
            <Linha termo="Agendada em" valor={dataHoraCurta(coleta.createdAt)} />
          </dl>

          {!cancelada &&
            (confirmando ? (
              <div className="mt-3 rounded-lg border border-amber-200 bg-amber-50 p-2.5">
                <p className="mb-2 flex gap-1.5 text-[11px] leading-snug text-amber-900">
                  <TriangleAlert className="mt-0.5 h-3.5 w-3.5 shrink-0" />
                  <span>
                    Isto marca a coleta como cancelada <strong>só aqui</strong>. A{" "}
                    {NOME_CARRIER[coleta.carrier] ?? coleta.carrier} não é avisada e o
                    motorista ainda vai ao endereço — ligue para a transportadora com o
                    número de confirmação.
                  </span>
                </p>
                <div className="flex gap-2">
                  <button
                    onClick={confirmar}
                    disabled={cancelando}
                    className="h-8 flex-1 rounded-md bg-red-600 text-xs font-semibold text-white disabled:opacity-40"
                  >
                    {cancelando ? "Cancelando…" : "Entendi, cancelar"}
                  </button>
                  <button
                    onClick={() => setConfirmando(false)}
                    className="h-8 flex-1 rounded-md border border-slate-200 bg-white text-xs text-slate-600"
                  >
                    Voltar
                  </button>
                </div>
              </div>
            ) : (
              <button
                onClick={() => setConfirmando(true)}
                className="mt-3 text-xs font-medium text-red-600 transition-colors hover:text-red-700"
              >
                Cancelar esta coleta
              </button>
            ))}
        </div>
      )}
    </div>
  );
};

const Linha = ({
  termo,
  valor,
  mono,
}: {
  termo: string;
  valor: string;
  mono?: boolean;
}) => (
  <div className="flex items-start justify-between gap-4">
    <dt className="shrink-0 text-slate-500">{termo}</dt>
    <dd
      className={`text-right font-medium text-slate-800 ${mono ? "font-mono text-[11px]" : ""}`}
    >
      {valor}
    </dd>
  </div>
);

const EstadoVazio = ({
  aguardando,
  onAgendar,
}: {
  aguardando: number | null;
  onAgendar: () => void;
}) => (
  <div className="flex flex-col items-center px-6 py-12 text-center">
    <div className="mb-4 grid h-14 w-14 place-items-center rounded-2xl bg-blue-50 text-brand-primary">
      <CalendarPlus className="h-6 w-6" />
    </div>
    <h3 className="text-base font-semibold text-slate-900">Nenhuma coleta agendada</h3>
    <p className="mt-1 max-w-[42ch] text-xs leading-relaxed text-slate-400">
      Quando você agendar, a data, o horário e o status de cada coleta aparecem aqui.
    </p>

    <div className="mt-5 flex w-full max-w-md items-center justify-between gap-3 rounded-lg border border-amber-200 bg-amber-50 px-3 py-2.5">
      <p className="flex items-center gap-2 text-left text-xs text-amber-900">
        <Package className="h-4 w-4 shrink-0" />
        {aguardando === null ? (
          <span>Agende a coleta para os envios com etiqueta emitida.</span>
        ) : (
          <span>
            <strong>
              {aguardando} {aguardando === 1 ? "envio" : "envios"} com etiqueta
            </strong>{" "}
            {aguardando === 1 ? "está esperando" : "estão esperando"} coleta.
          </span>
        )}
      </p>
      <button
        onClick={onAgendar}
        className="shrink-0 rounded-lg bg-brand-sidebar px-3 py-2 text-xs font-semibold text-white transition-opacity hover:opacity-90"
      >
        Agendar coleta
      </button>
    </div>
  </div>
);
